import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Lock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { zodResolver } from "@hookform/resolvers/zod";

export interface Suggestion {
  name: string;
  location: string;
  image_urls: string[];
  price: { total_price: number | null; currency: string } | null;
  description: string;
}

interface SuggestionsSubscribeWallProps {
  onSubmit: (data: { workEmail: string; phoneNumber: string; firstName: string; lastName: string }) => void;
  onPrevious?: () => void;
  isSubmitting?: boolean;
  suggestions: Suggestion[];
}

// Generic email detection
const isGenericEmail = (email: string): boolean => {
  const localPart = email.split('@')[0]?.toLowerCase();
  if (!localPart) return false;
  const genericPrefixes = [
    "info", "contact", "admin", "hello", "bonjour", "accueil", "reception",
    "direction", "service", "support", "commercial", "communication", "comm",
    "marketing", "rh", "hr", "comptabilite", "compta", "facturation",
    "billing", "sales", "office", "bureau", "secretariat", "general",
    "courrier", "mail", "team", "equipe", "event", "events", "evenement",
    "evenements", "booking", "reservation", "reservations", "projet",
    "projets", "gestion", "management", "partenariat", "partenariats",
    "presse", "media", "medias", "formation", "formations", "recrutement",
    "talent", "talents", "achats", "procurement", "logistique", "supply",
    "technique", "tech", "it", "dsi", "finance", "finances", "juridique",
    "legal", "qualite", "quality", "production", "operations", "ops",
    "noreply", "no-reply", "do-not-reply", "postmaster", "webmaster",
    "abuse", "root", "sav", "helpdesk", "help", "assistance",
    "assistant", "assistante", "directeur", "directrice", "responsable",
    "coordinateur", "coordinatrice", "stagiaire", "president", "presidente",
    "gerant", "gerante", "secretaire", "comptable", "tresorier", "tresoriere",
    "delegue", "deleguee", "charge", "chargee", "adjoint", "adjointe",
    "referent", "referente", "animateur", "animatrice", "conseiller",
    "conseillere", "ingenieur", "technicien", "technicienne", "superviseur",
    "manager", "chef", "maire", "notaire", "avocat", "avocate",
    "medecin", "pharmacien", "pharmacienne", "greffier", "greffiere",
    "intendant", "intendante", "econome", "standardiste", "regulateur",
    "test"
  ];
  const segments = localPart.split(/[.\-_]/);
  return segments.some(seg => genericPrefixes.includes(seg));
};

const FREE_DOMAINS = ["gmail.com", "googlemail.com", "hotmail.com", "hotmail.fr", "outlook.com", "outlook.fr", "live.com", "live.fr", "msn.com", "yahoo.com", "yahoo.fr", "ymail.com", "rocketmail.com", "icloud.com", "me.com", "mac.com", "aol.com", "aol.fr", "laposte.net", "orange.fr", "wanadoo.fr", "sfr.fr", "neuf.fr", "club-internet.fr", "cegetel.net", "numericable.fr", "free.fr", "aliceadsl.fr", "libertysurf.fr", "infonie.fr", "bbox.fr", "tele2.fr", "protonmail.com", "proton.me", "pm.me", "zoho.com", "gmx.fr", "gmx.com", "gmx.net", "mail.com", "yandex.com", "yandex.fr", "inbox.com", "bluewin.ch", "t-online.de", "web.de", "posteo.net", "caramail.com", "netcourrier.com", "keemail.me", "tutanota.com", "ueir.jci"];

// Placeholder blurred suggestion cards
const PlaceholderCard = () => (
  <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
    <Skeleton className="aspect-[4/3] w-full" />
    <div className="p-3 space-y-2">
      <Skeleton className="h-4 w-3/4" />
      <div className="flex items-center gap-1">
        <MapPin className="h-3 w-3 text-muted-foreground" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-2/3" />
      <Skeleton className="h-4 w-1/3 mt-1" />
    </div>
  </div>
);

// Real suggestion card (blurred)
const SuggestionCard: React.FC<{ suggestion: Suggestion }> = ({ suggestion }) => (
  <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
    <div className="aspect-[4/3] overflow-hidden">
      <img
        src={suggestion.image_urls?.[0] || "/placeholder.svg"}
        alt={suggestion.name}
        className="w-full h-full object-cover"
      />
    </div>
    <div className="p-3 space-y-1">
      <h4 className="font-semibold text-sm truncate">{suggestion.name}</h4>
      {suggestion.location && (
        <div className="flex items-center gap-1 text-muted-foreground text-xs">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{suggestion.location}</span>
        </div>
      )}
      <p className="text-xs text-muted-foreground line-clamp-2">{suggestion.description}</p>
      <div className="text-sm font-semibold">
        {suggestion.price?.total_price != null
          ? `${suggestion.price.total_price.toLocaleString("fr-FR")} ${suggestion.price.currency || "€"}`
          : "Prix sur demande"}
      </div>
    </div>
  </div>
);

const SuggestionsSubscribeWall: React.FC<SuggestionsSubscribeWallProps> = ({
  onSubmit,
  onPrevious,
  isSubmitting,
  suggestions
}) => {
  const [showNameFields, setShowNameFields] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const schema = z.object({
    workEmail: z.string().min(1, "L'email est requis").email("Adresse e-mail incorrecte").refine(email => {
      const domain = email.split('@')[1];
      return !FREE_DOMAINS.includes(domain?.toLowerCase());
    }, "Veuillez utiliser votre email professionnel"),
  });

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { workEmail: "" },
    mode: "onChange"
  });

  const hasError = !!form.formState.errors.workEmail;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setValue("workEmail", value);
    if (value && form.formState.errors.workEmail) {
      form.clearErrors("workEmail");
    }
    if (value.includes('@') && value.split('@')[1]?.length > 0) {
      setShowNameFields(isGenericEmail(value));
    } else {
      setShowNameFields(false);
    }
  };

  const handleSubmit = (data: FormValues) => {
    onSubmit({
      workEmail: data.workEmail,
      phoneNumber: "",
      firstName: showNameFields ? firstName : "",
      lastName: showNameFields ? lastName : ""
    });
  };

  // Suggestions loaded - show blurred results + modern subscribe wall
  return (
    <div className="relative h-full flex flex-col">
      {/* Blurred suggestion cards background */}
      <div className="absolute inset-0 overflow-hidden p-4 pt-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 blur-[6px] pointer-events-none select-none opacity-60">
          {suggestions.length > 0
            ? suggestions.slice(0, 6).map((s, i) => <SuggestionCard key={i} suggestion={s} />)
            : Array.from({ length: 6 }).map((_, i) => <PlaceholderCard key={i} />)
          }
        </div>
      </div>

      {/* Subscribe wall overlay */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-5 md:p-6 max-w-md w-full mx-auto border border-border/50">
          <div className="text-center mb-4">
            
            <h2 className="text-lg font-semibold text-foreground">Vos lieux personnalisés vous attendent</h2>
            <p className="text-sm text-muted-foreground">Entrez votre email professionnel pour débloquer votre sélection</p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
              <FormField control={form.control} name="workEmail" render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="prenom.nom@entreprise.com"
                      className="text-base h-12 rounded-xl border-border/70 focus:border-cta-green focus:ring-cta-green/20 transition-all"
                      {...field}
                      onChange={handleEmailChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              {showNameFields && (
                <div className="space-y-1.5 animate-fade-in">
                  <p className="text-xs text-muted-foreground italic">
                    Vous utilisez un email générique ? Partagez votre prénom et nom pour fluidifier nos échanges
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="text"
                      placeholder="Prénom"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="text-base h-12 rounded-xl"
                    />
                    <Input
                      type="text"
                      placeholder="Nom"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="text-base h-12 rounded-xl"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className={`w-full h-12 text-base font-semibold rounded-xl transition-all duration-200 ${
                  hasError
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 shadow-md shadow-cta-green/20 hover:shadow-lg hover:shadow-cta-green/30'
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Envoi...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Débloquer mes suggestions
                  </span>
                )}
              </Button>
            </form>
          </Form>

          <p className="text-[11px] text-muted-foreground/70 text-center mt-3">
            Gratuit et sans engagement · Vos données restent confidentielles
          </p>
        </div>

      </div>
    </div>
  );
};

export default SuggestionsSubscribeWall;
